import { Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import { DialogService } from '@shared/services/dialog.service';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Badge } from '../../../../../shared/components/ui/badge/badge';
import { DeleteTrigger } from '../../../../../shared/components/ui/delete-trigger/delete-trigger';
import { ButtonComponent } from '@shared/components/ui/button/button.component';
import { ErrorService } from '../../../../../shared/services/error.service';
import { SnackbarService } from '@shared/services/snackbar.service';
import { CrearContactoAdministrativo } from './crear-contacto-administrativo/crear-contacto-administrativo';
import { DetalleContactoAdministrativo } from './detalle-contacto-administrativo/detalle-contacto-administrativo';
import { ContactoAdministrativo } from './contactos-administrativos.interface';
import { ContactosAdministrativosService } from './contactos-administrativos.service';
import {
  DataTable,
  TableColumn,
  TableCellDirective,
  Pagination,
  SearchInput,
  FilterSelect,
  FilterOption,
  FiltersContainer
} from '../../../../../shared/components/ui/list-components';
import { CombosService } from '../../../../../shared/services/combos.service';

@Component({
  selector: 'app-contactos-administrativos',
  imports: [
    HasPermissionDirective,
    Badge,
    DeleteTrigger,
    MatIcon,
    MatTooltip,
    ButtonComponent,
    DataTable,
    TableCellDirective,
    Pagination,
    SearchInput,
    FilterSelect,
    FiltersContainer
  ],
  templateUrl: './contactos-administrativos.html',
})
export class ContactosAdministrativos implements OnChanges {
  @Input() cliClienteId: number | null = null;

  loading = false;
  deleting = false;
  deletingIndex: number | null = null;
  contactosAdministrativos: ContactoAdministrativo[] = [];

  // Paginación
  currentPage = 1;
  lastPage = 1;
  perPage = 15;
  total = 0;

  // Filtros
  searchTerm = '';
  selectedEstado: string | number = '';
  selectedTipoContacto: string | number = '';
  selectedOrigen: string | number = '';

  // Columnas de la tabla
  columns: TableColumn[] = [
    { key: 'nombre', header: 'Nombre' },
    { key: 'contacto', header: 'Contacto' },
    { key: 'origen', header: 'Origen' },
    { key: 'estado', header: 'Estado' },
    { key: 'tipo', header: 'Tipo' },
    { key: 'acciones', header: 'Acciones' }
  ];

  // Opciones de filtros
  estadoOptions: FilterOption[] = [
    { value: 'true', label: 'Activo' },
    { value: 'false', label: 'Inactivo' }
  ];

  tipoContactoOptions: FilterOption[] = [];

  origenOptions: FilterOption[] = [
    { value: 'A', label: 'Administrativo' },
    { value: 'E', label: 'De establecimiento' }
  ];

  private contactosAdministrativosService = inject(ContactosAdministrativosService);
  private combosService = inject(CombosService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private dialogService = inject(DialogService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cliClienteId'] && this.cliClienteId) {
      this.loadCombos();
      this.getContactos();
    }
  }

  loadCombos(): void {
    // Cargar tipos de contacto
    this.combosService.getEstablecimientosTiposContactosCombo().subscribe({
      next: (response) => {
        if (response.status) {
          this.tipoContactoOptions = response.data.map((item) => ({
            value: item.id.toString(),
            label: item.nombre
          }));
        }
      },
      error: (error) => this.errorService.handleError(error)
    });
  }

  getContactos(page: number = 1): void {
    if (!this.cliClienteId) {
      this.contactosAdministrativos = [];
      this.total = 0;
      this.lastPage = 1;
      this.currentPage = 1;
      return;
    }

    this.loading = true;

    // Construir filtros para enviar al backend
    const filters = {
      cli_cliente_id: this.cliClienteId,
      page: page,
      per_page: this.perPage,
      search: this.searchTerm || undefined,
      activo: this.selectedEstado !== '' ? this.selectedEstado.toString() : undefined,
      origen: this.selectedOrigen ? this.selectedOrigen.toString() : undefined,
      param_establecimiento_tipo_contacto_id: this.selectedTipoContacto ? this.selectedTipoContacto.toString() : undefined
    };

    this.contactosAdministrativosService
      .index(filters)
      .subscribe({
        next: (response) => {
          if (response.status) {
            this.contactosAdministrativos = response.data.data;
            this.currentPage = response.data.pagination.current_page;
            this.lastPage = response.data.pagination.last_page;
            this.total = response.data.pagination.total;
          }
        },
        error: (error) => {
          this.errorService.handleError(error);
        },
      })
      .add(() => {
        this.loading = false;
      });
  }

  // Handlers de filtros
  onSearchChange(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.currentPage = 1;
    this.getContactos();
  }

  onEstadoChange(value: string | number): void {
    this.selectedEstado = value;
    this.currentPage = 1;
    this.getContactos();
  }

  onTipoContactoChange(value: string | number): void {
    this.selectedTipoContacto = value;
    this.currentPage = 1;
    this.getContactos();
  }

  onOrigenChange(value: string | number): void {
    this.selectedOrigen = value;
    this.currentPage = 1;
    this.getContactos();
  }

  get hasActiveFilters(): boolean {
    return !!this.searchTerm || !!this.selectedEstado || !!this.selectedTipoContacto || !!this.selectedOrigen;
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.selectedEstado = '';
    this.selectedTipoContacto = '';
    this.selectedOrigen = '';
    this.currentPage = 1;
    this.getContactos();
  }

  // Handlers de paginación
  onPageChange(page: number): void {
    this.getContactos(page);
  }

  onPerPageChange(perPage: number): void {
    this.perPage = perPage;
    this.currentPage = 1;
    this.getContactos(1);
  }

  // Handler de click en fila
  onRowClick(event: { item: ContactoAdministrativo; index: number }): void {
    this.openViewModal(event.item.id);
  }

  openCreateModal(): void {
    if (!this.cliClienteId) {
      this.snackbarService.showSnackBar(
        'Debe seleccionar un cliente antes de agregar contactos'
      );
      return;
    }

    this.dialogService
      .openForm(CrearContactoAdministrativo, {
        size: 'md',
        data: this.cliClienteId,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.getContactos();
        }
      });
  }

  openViewModal(idContacto: number): void {
    this.dialogService
      .openForm(DetalleContactoAdministrativo, {
        size: 'md',
        data: idContacto,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.getContactos();
        }
      });
  }

  delete(id: number, index: number): void {
    this.deleting = true;
    this.deletingIndex = index;

    this.contactosAdministrativosService
      .destroy(id)
      .subscribe({
        next: (response) => {
          if (response.status) {
            this.snackbarService.showSnackBar('Contacto eliminado correctamente');
            this.getContactos(this.currentPage);
          }
        },
        error: (error) => {
          this.errorService.handleError(error);
        },
      })
      .add(() => {
        this.deleting = false;
        this.deletingIndex = null;
      });
  }
}
